/**
 * Password Strength Indicator
 * Visual indicator showing password strength from red to green
 * 
 * @author Varun Pandey
 */

import React from 'react';
import { cn } from '../../utils/cn';

interface PasswordStrengthProps {
  password: string;
  className?: string;
}

export const PasswordStrength: React.FC<PasswordStrengthProps> = ({ password, className }) => {
  const checkPasswordStrength = (pwd: string) => {
    if (!pwd) return { strength: 0, color: '#EF4444', requirements: [] };

    const requirements = [
      { met: /[A-Z]/.test(pwd), label: 'One uppercase letter' },
      { met: /[0-9]/.test(pwd), label: 'One number' },
      { met: /[^A-Za-z0-9]/.test(pwd), label: 'One special character' },
    ];

    const metCount = requirements.filter((r) => r.met).length;
    const strength = metCount / requirements.length;

    let color = '#EF4444'; // Red
    if (strength >= 1) {
      color = '#10B981'; // Green
    } else if (strength >= 0.66) {
      color = '#F59E0B'; // Yellow/Orange
    } else if (strength >= 0.33) {
      color = '#F97316'; // Orange
    }

    return { strength, color, requirements };
  };

  const { strength, color, requirements } = checkPasswordStrength(password);

  if (!password) return null;

  return (
    <div className={cn('space-y-1.5', className)}>
      {/* Progress bar */}
      <div className="h-1 w-full overflow-hidden rounded-full bg-[#2C2C2E]">
        <div
          className="h-full transition-all duration-300 ease-out"
          style={{
            width: `${strength * 100}%`,
            backgroundColor: color,
          }}
        />
      </div>

      {/* Requirements list - compact horizontal layout */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {requirements.map((req, index) => (
          <div key={index} className="flex items-center gap-1.5 text-xs">
            <div
              className={cn(
                'h-1 w-1 rounded-full flex-shrink-0',
                req.met ? 'bg-[#10B981]' : 'bg-[#8E8E93]'
              )}
            />
            <span className={cn(req.met ? 'text-[#10B981]' : 'text-[#8E8E93]')}>
              {req.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
